import React, { useState, useEffect } from 'react';
import { costCatalogApi } from '@/api';
import { ReportCostSummary } from '@/types';
import { formatCostRange, formatCurrency } from '@/utils/helpers';
import { SEVERITY, SEVERITY_HEX, SeverityKey } from '@/ui/severity';

const SEVERITY_ORDER: SeverityKey[] = ['safety_concern', 'recommendation', 'observation'];

interface CostSummaryPanelProps {
  reportId: string;
  refreshKey?: number;
}

const CostSummaryPanel: React.FC<CostSummaryPanelProps> = ({ reportId, refreshKey }) => {
  const [summary, setSummary] = useState<ReportCostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadSummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, refreshKey]);

  const loadSummary = async () => {
    try {
      const response = await costCatalogApi.getReportSummary(reportId);
      if (response.success && response.data) {
        setSummary(response.data);
      }
    } catch {
      // Summary is optional — don't show errors
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!summary || summary.itemCount === 0) return null;
  if (summary.totalLow === 0 && summary.totalHigh === 0) return null;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg mt-6">
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-100 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Estimated Repair Costs</h3>
            <p className="text-lg font-bold text-slate-900">
              {formatCostRange(summary.totalLow, summary.totalHigh)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{summary.itemCount} item{summary.itemCount !== 1 ? 's' : ''}</span>
          <svg
            className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* By Section */}
          {summary.bySection.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">By Section</h4>
              <div className="space-y-1">
                {summary.bySection.map((section, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600">{section.title}</span>
                    <span className="text-slate-800 font-medium">
                      {formatCostRange(section.totalLow, section.totalHigh)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By Severity */}
          {Object.keys(summary.bySeverity).length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">By Severity</h4>
              <div className="space-y-1">
                {SEVERITY_ORDER.map((key) => {
                  const data = summary.bySeverity[key];
                  if (!data || data.itemCount === 0) return null;
                  return (
                    <div key={key} className="flex justify-between text-sm">
                      <span style={{ color: SEVERITY_HEX[key].ink }}>
                        {SEVERITY[key].label} ({data.itemCount})
                      </span>
                      <span className="text-slate-800 font-medium">
                        {formatCostRange(data.totalLow, data.totalHigh)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Midpoint note */}
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-200">
            Midpoint estimate: {formatCurrency(summary.totalMidpoint)}. Actual costs may vary based on local conditions and contractor rates.
          </p>
        </div>
      )}
    </div>
  );
};

export default CostSummaryPanel;
